import { useState } from 'react';
import { CircleDollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const PRICING_MODELS = [
  { key: 'fixed_price', labelKey: 'afi_model_fixed' },
  { key: 'market_indexed', labelKey: 'afi_model_indexed' },
  { key: 'formula_based', labelKey: 'afi_model_formula' },
  { key: 'feed_in_tariff', labelKey: 'afi_model_feed_in' },
  { key: 'custom', labelKey: 'afi_model_custom' },
];

interface FinancialTerm {
  id: string;
  currency: string;
  pricingModel: string;
  priceFormula?: string;
  paymentTerms: string;
  settlementPeriod: string;
}

interface Props {
  assetId: string;
  terms?: FinancialTerm[];
  onSaved: () => void;
}

const PRICING_LABEL_KEYS: Record<string, string> = {
  fixed_price: 'afi_model_fixed',
  market_indexed: 'afi_model_indexed',
  formula_based: 'afi_model_formula',
  feed_in_tariff: 'afi_model_feed_in',
  custom: 'afi_model_custom',
};

export default function TabFinancial({ assetId, terms = [], onSaved }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(terms.length === 0);
  const [form, setForm] = useState({
    currency: 'UAH',
    pricingModel: '',
    priceFormula: '',
    paymentTerms: '',
    settlementPeriod: '',
  });

  const handleAdd = async () => {
    if (!form.currency || !form.pricingModel) {
      addToast(t('afi_required'), 'warning');
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        currency: form.currency,
        pricingModel: form.pricingModel,
      };
      if (form.priceFormula) payload.priceFormula = form.priceFormula;
      if (form.paymentTerms) payload.paymentTerms = form.paymentTerms;
      if (form.settlementPeriod) payload.settlementPeriod = form.settlementPeriod;

      await api.post(`/assets/${assetId}/financial-terms`, payload);
      addToast(t('afi_added_ok'), 'success');
      setForm({ currency: 'UAH', pricingModel: '', priceFormula: '', paymentTerms: '', settlementPeriod: '' });
      setShowForm(false);
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, t('afi_add_failed'));
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const inputBg = 'var(--surface-solid)';
  const inputBorder = 'var(--line-strong)';
  const sectionBg = 'var(--surface-2)';
  const sectionBorder = 'var(--line)';
  const cardBg = 'var(--surface-solid)';
  const inputClass = 'w-full px-3 py-2 rounded-md text-sm outline-none transition-colors';
  const inputStyle = { background: inputBg, border: `1px solid ${inputBorder}`, color: textMain };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
            <CircleDollarSign size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('afi_title')}</h3>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Pricing model, payment terms and settlement periods</p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
            style={{ border: '1px solid var(--accent)', color: 'var(--accent)' }}
          >
            <i className="ri-add-line"></i> Add Terms
          </button>
        )}
      </div>

      {/* Existing terms */}
      {terms.length > 0 && (
        <div className="space-y-2 mb-5">
          {terms.map((ft) => (
            <div key={ft.id} className="p-4 rounded-lg" style={{ border: '1px solid var(--line)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium" style={{ color: textMain }}>
                    {PRICING_LABEL_KEYS[ft.pricingModel] ? t(PRICING_LABEL_KEYS[ft.pricingModel]) : ft.pricingModel}
                  </span>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--text)' }}>
                  {ft.currency}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                {ft.paymentTerms && <span><i className="ri-calendar-check-line mr-1"></i>{ft.paymentTerms}</span>}
                {ft.settlementPeriod && <span><i className="ri-time-line mr-1"></i>{ft.settlementPeriod}</span>}
              </div>
              {ft.priceFormula && (
                <div className="mt-2 px-3 py-2 rounded-md text-xs tabular-nums" style={{ background: 'var(--surface-2)', color: 'var(--text)' }}>
                  {ft.priceFormula}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="p-5 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold" style={{ color: textMain }}>{t('afi_new')}</h4>
            {terms.length > 0 && (
              <button onClick={() => setShowForm(false)} className="text-xs cursor-pointer" style={{ color: 'var(--text-muted)' }}>
                {t('common_cancel')}
              </button>
            )}
          </div>

          {apiError && (
            <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
              <i className="ri-error-warning-line mr-2"></i>{apiError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                {t('afi_currency')} <span style={{ color: 'var(--text-dim)' }}>*</span>
              </label>
              <select
                value={form.currency}
                onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="UAH">{t('afi_cur_uah')}</option>
                <option value="EUR">{t('afi_cur_eur')}</option>
                <option value="USD">{t('afi_cur_usd')}</option>
                <option value="PLN">{t('afi_cur_pln')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                {t('afi_model')} <span style={{ color: 'var(--text-dim)' }}>*</span>
              </label>
              <select
                value={form.pricingModel}
                onChange={(e) => setForm((prev) => ({ ...prev, pricingModel: e.target.value }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="">{t('common_select_model')}</option>
                {PRICING_MODELS.map((m) => (
                  <option key={m.key} value={m.key}>{t(m.labelKey)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>{t('afi_payment')}</label>
              <input
                type="text"
                value={form.paymentTerms}
                onChange={(e) => setForm((prev) => ({ ...prev, paymentTerms: e.target.value }))}
                placeholder={`${t('common_eg')} Net 30, Monthly`}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>{t('afi_period')}</label>
              <input
                type="text"
                value={form.settlementPeriod}
                onChange={(e) => setForm((prev) => ({ ...prev, settlementPeriod: e.target.value }))}
                placeholder={`${t('common_eg')} Monthly, Quarterly`}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                {t('afi_formula')}
                <span className="ml-1 font-normal" style={{ color: 'var(--text-muted)' }}>{t('afi_formula_hint')}</span>
              </label>
              <input
                type="text"
                value={form.priceFormula}
                onChange={(e) => setForm((prev) => ({ ...prev, priceFormula: e.target.value }))}
                placeholder={`${t('common_eg')} DAM_price * 0.95 + 50`}
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleAdd}
              disabled={saving || !form.currency || !form.pricingModel}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i> Adding...</>
              ) : (
                <><i className="ri-add-line"></i> Add Terms</>
              )}
            </button>
          </div>
        </div>
      )}

      {terms.length === 0 && !showForm && (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--surface-2)' }}>
            <CircleDollarSign size={20} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>{t('afi_empty')}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('afi_empty_hint')}</p>
        </div>
      )}
    </div>
  );
}
